import React, { useEffect, useRef, useState } from 'react';

const ASSET_PATH = '/images';

export function ReferralListSheet({ isVisible, onClose, referrals = [], totalEarnings }) {
    const sheetRef = useRef(null);
    const [isOpen, setIsOpen] = useState(false);
    const [startY, setStartY] = useState(null);
    const [currentY, setCurrentY] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [expandedReferral, setExpandedReferral] = useState(null);

    useEffect(() => {
        if (isVisible) {
            setTimeout(() => setIsOpen(true), 10);
            document.body.style.overflow = 'hidden';
            document.body.classList.add('sheet-open');
        } else {
            setIsOpen(false);
            document.body.style.overflow = '';
            document.body.classList.remove('sheet-open');
        }

        return () => {
            document.body.style.overflow = '';
            document.body.classList.remove('sheet-open');
        };
    }, [isVisible]);

    // Обработчики для свайпа
    const handleTouchStart = (e) => {
        setStartY(e.touches[0].clientY);
    };

    const handleTouchMove = (e) => {
        if (startY === null || !sheetRef.current) return;

        const currentTouchY = e.touches[0].clientY;
        const diff = currentTouchY - startY;

        if (diff > 0) {
            e.preventDefault();
            setCurrentY(diff);
            sheetRef.current.style.transform = `translateY(${diff}px)`;
        }
    };

    const handleTouchEnd = (e) => {
        if (startY === null) return;

        const diff = currentY || 0;

        if (diff > 100) {
            handleClose();
        } else {
            if (sheetRef.current) {
                sheetRef.current.style.transform = '';
            }
        }

        setStartY(null);
        setCurrentY(null);
    };

    const handleMouseDown = (e) => {
        setStartY(e.clientY);

        const handleMouseMove = (e) => {
            if (startY === null || !sheetRef.current) return;

            const diff = e.clientY - startY;

            if (diff > 0) {
                setCurrentY(diff);
                sheetRef.current.style.transform = `translateY(${diff}px)`;
            }
        };

        const handleMouseUp = (e) => {
            const diff = currentY || 0;

            if (diff > 100) {
                handleClose();
            } else {
                if (sheetRef.current) {
                    sheetRef.current.style.transform = '';
                }
            }

            setStartY(null);
            setCurrentY(null);

            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    const handleClose = () => {
        setIsOpen(false);
        setTimeout(() => {
            onClose();
        }, 300);
    };

    // Получение инициалов для аватара
    const getInitials = (name) => {
        if (!name) return '??';
        return name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
    };

    // Получение цвета для аватара на основе имени
    const getAvatarColor = (name) => {
        const colors = ['#f67ea0', '#56a8f1', '#a387f2', '#f57959', '#62c14e', '#9b81ef', '#fcb054', '#8ccd5e'];
        const index = (name?.length || 0) % colors.length;
        return colors[index];
    };

    // Форматирование даты
    const formatDate = (dateString) => {
        if (!dateString) return 'Неизвестно';
        const now = new Date();
        const date = new Date(dateString);
        const diffTime = Math.abs(now - date);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays < 7) return `${diffDays} дней назад`;
        if (diffDays < 30) return `${Math.floor(diffDays / 7)} недель назад`;
        if (diffDays < 365) return `${Math.floor(diffDays / 30)} месяцев назад`;
        return `${Math.floor(diffDays / 365)} лет назад`;
    };

    // Безопасное форматирование суммы
    const formatEarnings = (amount) => {
        const num = Number(amount);
        if (isNaN(num)) return '0.00 ₽';
        return `${num.toFixed(2)} ₽`;
    };

    // Безопасное получение значений
    const safeTotalEarnings = Number(totalEarnings) || 0;

    // Безопасные рефералы с правильными полями
    const safeReferrals = referrals
        .map(ref => ({
            ...ref,
            earnings: Number(ref.earnings) || 0,
            topupsCount: Number(ref.topupsCount) || 0,
            totalTopups: Number(ref.totalTopups) || 0,
            name: ref.name || 'Пользователь',
            joinedAt: ref.joinedAt || new Date().toISOString()
        }))
        .sort((a, b) => b.earnings - a.earnings);

    // Фильтрация рефералов по поиску
    const filteredReferrals = safeReferrals.filter(ref =>
        ref.name?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    if (!isVisible) return null;

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 100000,
            pointerEvents: 'none',
        }}>
            {/* Затемнение */}
            <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                backdropFilter: 'blur(3px)',
                opacity: isOpen ? 1 : 0,
                transition: 'opacity 0.3s ease',
                pointerEvents: isOpen ? 'auto' : 'none',
            }} onClick={handleClose} />

            {/* Шторка */}
            <div
                ref={sheetRef}
                className='refsheet'
                style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    backgroundColor: '#0b1217',
                    borderTopLeftRadius: '32px',
                    borderTopRightRadius: '32px',
                    boxShadow: '0 -4px 30px rgba(0, 0, 0, 0.5)',
                    pointerEvents: 'auto',
                    transform: isOpen ? 'translateY(0)' : 'translateY(100%)',
                    transition: currentY ? 'none' : 'transform 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
                    maxHeight: '80vh',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                {/* Фоновый вектор */}
                <img
                    src={`${ASSET_PATH}/0_596.svg`}
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: 'auto',
                        pointerEvents: 'none',
                        zIndex: 0,
                        opacity: 0.3,
                        filter: 'brightness(1.5)'
                    }}
                    alt="Background Pattern"
                />

                {/* Контент */}
                <div style={{
                    position: 'relative',
                    zIndex: 1,
                    padding: '20px 16px 0 16px',
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    overflow: 'hidden',
                }}>
                    {/* Ползунок для закрытия */}
                    <div
                        style={{
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'center',
                            padding: '8px 0 16px 0',
                            cursor: 'grab',
                            touchAction: 'none',
                            userSelect: 'none',
                        }}
                        onTouchStart={handleTouchStart}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd}
                        onMouseDown={handleMouseDown}
                    >
                        <div style={{
                            width: '40px',
                            height: '4px',
                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                            borderRadius: '2px',
                        }} />
                    </div>

                    {/* Поиск */}
                    <div style={{
                        backgroundColor: 'rgba(200, 255, 227, 0.1)',
                        backdropFilter: 'blur(1px)',
                        borderRadius: '16px',
                        height: '40px',
                        display: 'flex',
                        alignItems: 'center',
                        padding: '0 12px',
                        gap: '8px',
                        marginBottom: '16px',
                        flexShrink: 0,
                    }}>
                        <img
                            src={`${ASSET_PATH}/I0_238_637_3195.svg`}
                            style={{ width: '20px', height: '20px' }}
                            alt="Search"
                        />
                        <input
                            type="text"
                            placeholder="Поиск по имени..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            style={{
                                background: 'transparent',
                                border: 'none',
                                outline: 'none',
                                color: 'white',
                                fontSize: '13px',
                                width: '100%',
                            }}
                        />
                        {searchQuery && (
                            <button
                                onClick={() => setSearchQuery('')}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    color: 'rgba(255, 255, 255, 0.5)',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    padding: '0 4px',
                                }}
                            >
                                ✕
                            </button>
                        )}
                    </div>

                    {/* Заголовки списка */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '0 4px',
                        marginBottom: '12px',
                        flexShrink: 0,
                    }}>
                        <span style={{ color: 'white', fontSize: '12px' }}>Друг</span>
                        <span style={{ color: 'white', fontSize: '12px' }}>Заработано</span>
                    </div>

                    {/* Разделитель */}
                    <div style={{
                        height: '1px',
                        backgroundColor: 'rgba(200, 255, 227, 0.1)',
                        width: '100%',
                        marginBottom: '16px',
                        flexShrink: 0,
                    }} />

                    {/* Список рефералов */}
                    <div style={{
                        flex: 1,
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '12px',
                        paddingRight: '4px',
                        paddingBottom: '20px',
                    }}>
                        {safeReferrals.length === 0 ? (
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '40px 20px',
                                textAlign: 'center',
                            }}>
                                <p style={{ color: 'white', fontSize: '14px', marginBottom: '8px' }}>
                                    У вас пока нет рефералов
                                </p>
                                <p style={{ color: 'rgba(255, 255, 255, 0.4)', fontSize: '12px' }}>
                                    Пригласите друзей по ссылке выше
                                </p>
                            </div>
                        ) : filteredReferrals.length === 0 ? (
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '40px 20px',
                                textAlign: 'center',
                            }}>
                                <p style={{ color: 'white', fontSize: '14px', marginBottom: '8px' }}>
                                    Ничего не найдено
                                </p>
                                <p style={{ color: 'rgba(255, 255, 255, 0.4)', fontSize: '12px' }}>
                                    Попробуйте изменить поисковый запрос
                                </p>
                            </div>
                        ) : (
                            filteredReferrals.map((referral, index) => (
                                <div key={index}>
                                    <div
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            padding: '8px 4px',
                                            cursor: 'pointer',
                                        }}
                                        onClick={() => setExpandedReferral(expandedReferral === referral.id ? null : referral.id)}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                            <div style={{
                                                width: '44px',
                                                height: '44px',
                                                borderRadius: '50%',
                                                backgroundColor: getAvatarColor(referral.name),
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                color: 'white',
                                                fontSize: '12px',
                                                fontWeight: 500,
                                                flexShrink: 0,
                                            }}>
                                                {getInitials(referral.name)}
                                            </div>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                <span style={{ color: 'white', fontSize: '12px' }}>{referral.name}</span>
                                                <span style={{ color: 'rgba(255, 255, 255, 0.4)', fontSize: '11px' }}>
                                                    {formatDate(referral.joinedAt)}
                                                </span>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                            <img
                                                src={`${ASSET_PATH}/0_256.svg`}
                                                style={{ width: '20px', height: '20px' }}
                                                alt="Earnings"
                                            />
                                            <span style={{ color: '#32d755', fontSize: '12px', fontWeight: 500 }}>
                                                {formatEarnings(referral.earnings)}
                                            </span>
                                            <span style={{
                                                color: 'rgba(255, 255, 255, 0.4)',
                                                fontSize: '10px',
                                                marginLeft: '2px',
                                                transition: 'transform 0.2s',
                                                transform: expandedReferral === referral.id ? 'rotate(180deg)' : 'rotate(0deg)',
                                                display: 'inline-block',
                                            }}>
                                                ▼
                                            </span>
                                        </div>
                                    </div>

                                    {/* Детальная информация о реферале */}
                                    {expandedReferral === referral.id && (
                                        <div style={{
                                            marginTop: '8px',
                                            marginLeft: '56px',
                                            marginBottom: '4px',
                                            padding: '12px',
                                            backgroundColor: 'rgba(0, 0, 0, 0.3)',
                                            borderRadius: '12px',
                                            borderLeft: '2px solid #32d755',
                                        }}>
                                            <div style={{ marginBottom: '8px', display: 'flex', justifyContent: 'space-between' }}>
                                                <span style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '11px' }}>
                                                    Пополнений:
                                                </span>
                                                <span style={{ color: 'white', fontSize: '12px', fontWeight: 500 }}>
                                                    {referral.topupsCount}
                                                </span>
                                            </div>
                                            <div style={{ marginBottom: '8px', display: 'flex', justifyContent: 'space-between' }}>
                                                <span style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '11px' }}>
                                                    Общая сумма пополнений:
                                                </span>
                                                <span style={{ color: 'white', fontSize: '12px', fontWeight: 500 }}>
                                                    {formatEarnings(referral.totalTopups)}
                                                </span>
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <span style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '11px' }}>
                                                    Ваш заработок ({process.env.REFERRAL_PERCENT || 15}%):
                                                </span>
                                                <span style={{ color: '#32d755', fontSize: '12px', fontWeight: 600 }}>
                                                    {formatEarnings(referral.earnings)}
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}